import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import {scopedRepositoryQuery} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {queryForRepos} from '../task-chat-input-toolbar/TaskChatInputToolbar'

interface DestinationRepositoryRequest {
  query: string
  preferredOwner?: string
  includedRepository?: string
  rejectOnHttpError?: boolean
}

export async function loadDestinationRepositories({
  query,
  preferredOwner,
  includedRepository,
  rejectOnHttpError = false,
}: DestinationRepositoryRequest): Promise<AgentDropdownRepository[]> {
  const options = rejectOnHttpError ? {rejectOnHttpError: true} : undefined
  const load = (search: string, owner?: string) => queryForRepos(search, owner, includedRepository, options)
  let repositories: AgentDropdownRepository[]

  if (!preferredOwner || query.trim().includes('/')) {
    repositories = await load(query)
  } else {
    const preferredQuery = scopedRepositoryQuery(query, preferredOwner)
    const requests = [load(preferredQuery, preferredOwner), load(query)]
    let preferred: AgentDropdownRepository[]
    let global: AgentDropdownRepository[]
    if (rejectOnHttpError) {
      const [preferredResult, globalResult] = await Promise.all(requests)
      preferred = preferredResult ?? []
      global = globalResult ?? []
    } else {
      const results = await Promise.allSettled(requests)
      preferred = results[0]?.status === 'fulfilled' ? results[0].value : []
      global = results[1]?.status === 'fulfilled' ? results[1].value : []
    }

    const seen = new Set<number | string>()
    const preferredRepositories: AgentDropdownRepository[] = []
    const otherRepositories: AgentDropdownRepository[] = []
    for (const repo of [...preferred, ...global]) {
      const key = repo.id ?? repo.nameWithOwner.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      if (repo.owner.toLowerCase() === preferredOwner.toLowerCase()) {
        preferredRepositories.push(repo)
      } else {
        otherRepositories.push(repo)
      }
    }
    repositories = [...preferredRepositories, ...otherRepositories]
  }

  return repositories.filter(repo => repo.enabled)
}
